import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import ptLocale from "i18n-iso-countries/langs/pt.json";

countries.registerLocale(enLocale);
countries.registerLocale(ptLocale);

const GEO_URL = "https://unpkg.com/world-atlas@2/countries-110m.json";

// Rampa sequencial azul validada (ver skill de dataviz) — passo mais claro
// perto da superfície ("quase zero"), mais escuro = mais envios.
const SEQUENTIAL_BLUE = [
  "#cde2fb", "#b7d3f6", "#9ec5f4", "#86b6ef",
  "#6da7ec", "#5598e7", "#3987e5", "#2a78d6",
  "#256abf", "#1c5cab", "#184f95", "#104281", "#0d366b",
];
const NO_DATA_FILL = "#e2e8f0"; // slate-200

export type CountryShipments = { pais: string; total: number };

type Props = {
  data: CountryShipments[];
};

const WorldShipmentMap = ({ data }: Props) => {
  const byNumericId = new Map<string, { alpha2: string; total: number }>();
  let max = 0;

  for (const d of data) {
    const numeric = countries.alpha2ToNumeric(d.pais);
    if (!numeric) continue;
    byNumericId.set(numeric, { alpha2: d.pais, total: d.total });
    if (d.total > max) max = d.total;
  }

  const colorFor = (numericId: string) => {
    const entry = byNumericId.get(numericId);
    if (!entry || max === 0) return NO_DATA_FILL;
    // escala em raiz quadrada: comprime o topo (evita 1 país dominante deixar
    // todo o resto na cor mais clara) e ainda diferencia os países pequenos.
    const intensity = Math.sqrt(entry.total) / Math.sqrt(max);
    const stepIndex = Math.min(SEQUENTIAL_BLUE.length - 1, Math.floor(intensity * SEQUENTIAL_BLUE.length));
    return SEQUENTIAL_BLUE[stepIndex];
  };

  const countryName = (alpha2: string) => countries.getName(alpha2, "pt") || countries.getName(alpha2, "en") || alpha2;

  const top = [...data].sort((a, b) => b.total - a.total).slice(0, 8);

  return (
    <div className="flex flex-col md:flex-row gap-4 items-center">
      <div className="w-full md:w-2/3">
        <ComposableMap projectionConfig={{ scale: 130 }} style={{ width: "100%", height: "auto" }}>
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const entry = byNumericId.get(geo.id as string);
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={colorFor(geo.id as string)}
                    stroke="#fff"
                    strokeWidth={0.5}
                    style={{
                      default: { outline: "none" },
                      hover: { outline: "none", fill: entry ? "#0d366b" : NO_DATA_FILL },
                      pressed: { outline: "none" },
                    }}
                  >
                    <title>
                      {geo.properties?.name || "—"}
                      {entry ? `: ${entry.total} envio${entry.total === 1 ? "" : "s"}` : ": sem envios"}
                    </title>
                  </Geography>
                );
              })
            }
          </Geographies>
        </ComposableMap>
        <div className="flex items-center gap-2 mt-2 px-1">
          <span className="text-xs text-slate-400">Menos</span>
          <div className="flex h-2 flex-1 rounded-full overflow-hidden">
            {SEQUENTIAL_BLUE.map((c) => (
              <span key={c} style={{ background: c }} className="flex-1" />
            ))}
          </div>
          <span className="text-xs text-slate-400">Mais</span>
        </div>
      </div>

      <div className="w-full md:w-1/3">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Top destinos</p>
        <ul className="flex flex-col divide-y divide-slate-100">
          {top.map((c) => (
            <li key={c.pais} className="flex items-center justify-between py-1.5 text-sm">
              <span className="text-slate-700">{countryName(c.pais)}</span>
              <span className="font-semibold text-slate-900 tabular-nums">{c.total}</span>
            </li>
          ))}
          {!top.length && <li className="text-sm text-muted-foreground py-1.5">Sem dados no período.</li>}
        </ul>
      </div>
    </div>
  );
};

export default WorldShipmentMap;
